import { useState } from 'react';
import { apiGet, apiPatch, ApiError } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { StatusBadge } from '../components/StatusBadge';

/** GET/PATCH /pool-score/admin/config (PoolScoreConfigService / PoolScoreAdminController). */
interface PoolScoreWeights {
  sla: number;
  completion: number;
  satisfaction: number;
  responsiveness: number;
  volume: number;
  competency: number;
  profile: number;
}

interface PoolScoreConfig {
  weights: PoolScoreWeights;
  verifiedBonus: number;
}

const WEIGHT_LABELS: Record<keyof PoolScoreWeights, string> = {
  sla: 'SLA compliance',
  completion: 'Completion rate',
  satisfaction: 'User satisfaction',
  responsiveness: 'Responsiveness',
  volume: 'Volume',
  competency: 'Competency exam',
  profile: 'Profile completeness',
};

const WEIGHT_KEYS = Object.keys(WEIGHT_LABELS) as (keyof PoolScoreWeights)[];

/** One colour per component, so a segment in the composite bar is traceable to its row. */
const WEIGHT_COLORS: Record<keyof PoolScoreWeights, string> = {
  sla: '#0ea5e9',
  completion: '#10b981',
  satisfaction: '#93d5e1',
  responsiveness: '#f59e0b',
  volume: '#0284c7',
  competency: '#8b5cf6',
  profile: '#64748b',
};

/**
 * ADM ops — Pool Score config (gap #9, docs/pool-ranking.md §5): the specialist matching-pool
 * ranking algorithm's weights (backend/src/common/utils/specialist-pool-score.util.ts's
 * POOL_SCORE_WEIGHTS default) plus the verified-specialist bonus were only editable via
 * curl/Postman against PoolScoreAdminController — this is the first admin-portal UI for it.
 *
 * Weights are entered as raw 0-1 decimals (matching the backend's IPoolScoreWeights contract
 * exactly) rather than percentages, so what's typed here is what gets persisted, no unit
 * conversion to get wrong. A live sum indicator warns — but does not block — a save that
 * doesn't sum to 1.0, mirroring PoolScoreConfigService.updateWeights' own "persists anyway"
 * behavior server-side.
 */
export function PoolScoreConfigPage() {
  const { data: config, loading, error, reload } = useApiResource(
    () => apiGet<PoolScoreConfig>('/pool-score/admin/config'),
    [],
  );

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const draftFor = (key: keyof PoolScoreWeights): string =>
    key in drafts ? drafts[key] : config?.weights[key]?.toString() ?? '';
  const verifiedBonusDraft =
    'verifiedBonus' in drafts ? drafts.verifiedBonus : config?.verifiedBonus?.toString() ?? '';

  const parsedWeights: Partial<Record<keyof PoolScoreWeights, number>> = {};
  let hasInvalid = false;
  for (const key of WEIGHT_KEYS) {
    const raw = draftFor(key).trim();
    const n = Number(raw);
    if (raw === '' || !Number.isFinite(n) || n < 0) {
      hasInvalid = true;
    } else {
      parsedWeights[key] = n;
    }
  }
  const weightSum = WEIGHT_KEYS.reduce((sum, key) => sum + (parsedWeights[key] ?? 0), 0);
  const sumIsOff = !hasInvalid && Math.abs(weightSum - 1) > 0.001;

  const parsedVerifiedBonus = Number(verifiedBonusDraft.trim());
  const verifiedBonusInvalid =
    verifiedBonusDraft.trim() === '' || !Number.isFinite(parsedVerifiedBonus) || parsedVerifiedBonus < 0;

  const hasChanges =
    !!config &&
    (WEIGHT_KEYS.some((key) => key in drafts && drafts[key] !== config.weights[key].toString()) ||
      ('verifiedBonus' in drafts && drafts.verifiedBonus !== config.verifiedBonus.toString()));

  const save = async () => {
    if (!config) return;
    if (hasInvalid || verifiedBonusInvalid) {
      setActionError('Every field must be a number ≥ 0.');
      return;
    }
    setSaving(true);
    setActionError(null);
    try {
      const weightsChanged = WEIGHT_KEYS.some((key) => key in drafts);
      const bonusChanged = 'verifiedBonus' in drafts;
      if (weightsChanged) {
        await apiPatch('/pool-score/admin/config/weights', parsedWeights);
      }
      if (bonusChanged) {
        await apiPatch('/pool-score/admin/config/verified-bonus', {
          verifiedBonus: parsedVerifiedBonus,
        });
      }
      setDrafts({});
      setSavedAt(Date.now());
      reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Pool-score config update failed');
    } finally {
      setSaving(false);
    }
  };

  const showLoadingState = !config && loading;

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Pool Score Config</h1>
          <p>
            Runtime weights for the specialist matching-pool ranking algorithm — drives both the
            case-matching notification order and each specialist&rsquo;s dashboard rank. Changes
            take effect without a backend release.
          </p>
        </div>
      </div>

      <div className="form-card" style={{ maxWidth: 'none', marginBottom: '1rem', background: 'var(--surface-muted, var(--surface))' }}>
        <StatusBadge label="Live — affects real ranking" tone="warn" />
        <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
          These weights determine specialist notification order and dashboard rank for every
          specialist on the platform as soon as they&rsquo;re saved. Weights should sum to 1.0 —
          the backend will persist an off-sum save anyway (same as this page), so double-check
          before saving.
        </p>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {actionError && <div className="error-banner">{actionError}</div>}
      {savedAt && !actionError && (
        <div style={{ marginBottom: '1rem' }}>
          <StatusBadge label="Saved" tone="good" />
        </div>
      )}

      {showLoadingState ? (
        <div className="empty-state">Loading…</div>
      ) : !config ? (
        <div className="empty-state">Could not load pool-score config.</div>
      ) : (
        <>
          <div className="form-card" style={{ maxWidth: 'none' }}>
            <h2>Ranking weights</h2>
            <div className="weight-list" style={{ marginTop: '0.9rem' }}>
              {WEIGHT_KEYS.map((key) => {
                const n = parsedWeights[key] ?? 0;
                return (
                  <div className="weight-row" key={key}>
                    <label className="weight-row-name" htmlFor={`weight-${key}`}>
                      {WEIGHT_LABELS[key]}
                    </label>
                    {/* Width is the weight's share of 1.0, so a row that dominates the
                        ranking looks like it dominates. */}
                    <div className="weight-row-track">
                      <div
                        className="weight-row-fill"
                        style={{
                          width: `${Math.min(100, n * 100)}%`,
                          background: WEIGHT_COLORS[key],
                        }}
                      />
                    </div>
                    <input
                      id={`weight-${key}`}
                      type="number"
                      min={0}
                      max={1}
                      step="0.01"
                      value={draftFor(key)}
                      onChange={(e) => setDrafts((prev) => ({ ...prev, [key]: e.target.value }))}
                    />
                  </div>
                );
              })}
            </div>

            <div className="weight-total">
              <div className="weight-total-head">
                <span className="weight-total-label">Total</span>
                <StatusBadge
                  label={
                    hasInvalid
                      ? 'invalid'
                      : sumIsOff
                        ? `${weightSum.toFixed(3)} — should be 1.000`
                        : `${weightSum.toFixed(3)}`
                  }
                  tone={hasInvalid ? 'bad' : sumIsOff ? 'warn' : 'good'}
                />
              </div>
              {/* The whole point of the bar: it fills exactly when the weights sum to 1. */}
              <div className={sumIsOff && weightSum > 1 ? 'weight-stack over' : 'weight-stack'}>
                {WEIGHT_KEYS.map((key) => (
                  <div
                    key={key}
                    className="weight-stack-seg"
                    title={`${WEIGHT_LABELS[key]}: ${(parsedWeights[key] ?? 0).toFixed(3)}`}
                    style={{
                      width: `${Math.min(100, (parsedWeights[key] ?? 0) * 100)}%`,
                      background: WEIGHT_COLORS[key],
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="form-card" style={{ maxWidth: 'none' }}>
            <h2>Verified-specialist bonus</h2>
            <p className="form-hint" style={{ marginTop: '0.4rem' }}>
              Added to the final score on a 0-100 scale, after the weighted components — it is not
              part of the sum above.
            </p>
            <div className="setting-row" style={{ borderBottom: 'none', paddingBottom: 0 }}>
              <div className="setting-row-text">
                <label className="setting-row-name" htmlFor="verified-bonus">
                  Bonus points
                </label>
              </div>
              <div className="setting-row-control">
                <input
                  id="verified-bonus"
                  type="number"
                  min={0}
                  step="1"
                  value={verifiedBonusDraft}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, verifiedBonus: e.target.value }))}
                  style={{ width: '6rem', height: 32, padding: '0 0.5rem', textAlign: 'right' }}
                />
              </div>
            </div>
          </div>

          <div style={{ marginTop: '1rem' }}>
            <button
              className="btn btn-primary"
              disabled={saving || !hasChanges || hasInvalid || verifiedBonusInvalid}
              onClick={() => void save()}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
